import BigNumber from 'bignumber.js';

const toBN = (v) => {
  if (v === undefined || v === null || v === '--') return null;
  try {
    const n = v instanceof BigNumber ? v : new BigNumber(v);
    return n.isNaN() ? null : n;
  } catch {
    return null;
  }
};

/**
 * Formats an R-multiple for display: up to 2 decimals, trailing zeros trimmed —
 * so a 0.25R buffer reads "1.25R", not the rounded "1.3R", while a whole 3R
 * still reads "3R". Rounded HALF_UP explicitly: the app configures BigNumber
 * globally to ROUND_DOWN, which would turn a 1.9999999-style division remainder
 * into "1.99".
 */
export function formatR(value) {
  const bn = toBN(value);
  if (!bn) return '--';
  return bn.decimalPlaces(2, BigNumber.ROUND_HALF_UP).toFormat();
}

/**
 * Derives how far the liquidation price sits beyond the stop, expressed in R
 * (multiples of the stop distance), plus a plain-language message for the UI.
 *
 *   actualR    = |liq - stop| / |entry - stop|
 *   lossAtLiqR = 1 + actualR   (1R to the stop, then the buffer beyond it)
 *
 * The safety buffer is requested in R; normally actualR === requestedR. When the
 * Max Leverage cap (the exchange limit) binds, leverage is forced lower and
 * liquidation lands FURTHER than requested — surfaced calmly, since that's safer.
 *
 * Returns null when inputs aren't ready.
 */
export function getLiquidationBufferInfo({ liquidationPrice, stopLoss, entryPrice, requestedR, maxLeverage }) {
  const liq = toBN(liquidationPrice);
  const stop = toBN(stopLoss);
  const entry = toBN(entryPrice);
  const reqR = toBN(requestedR);
  const maxLev = toBN(maxLeverage);

  if (!liq || !stop || !entry) return null;

  const oneR = entry.minus(stop).abs();
  if (oneR.isZero()) return null;

  const actualR = liq.minus(stop).abs().dividedBy(oneR);
  const lossAtLiqR = actualR.plus(1);

  // Liquidation must sit on the FAR side of the stop (beyond it, never before).
  const isLong = stop.isLessThan(entry);
  const beyond = isLong ? liq.isLessThan(stop) : liq.isGreaterThan(stop);

  const eps = new BigNumber(0.05);
  let message = null;
  let tone = 'info'; // 'info' | 'warn' | 'danger'

  if (!beyond) {
    tone = 'danger';
    message = 'Liquidation is not beyond your stop — raise the safety buffer.';
  } else if (reqR && actualR.gt(reqR.plus(eps))) {
    const lev = maxLev ? maxLev.toFormat(0) : null;
    message = `${lev ? `This instrument allows max ${lev}× leverage, so ` : ''}liquidation sits ${formatR(actualR)}R beyond your stop — further than your ${formatR(reqR)}R buffer. That's fine: you exit at your stop (−1R), and liquidation is just a distant backstop, not your expected loss.`;
  } else if (reqR && actualR.lt(reqR.minus(eps))) {
    tone = 'warn';
    message = `Buffer limited to ${formatR(actualR)}R — this stop is too wide to place liquidation the full ${formatR(reqR)}R away; it's as far as the position allows.`;
  } else if (reqR && reqR.lt(new BigNumber(0.5))) {
    tone = 'warn';
    message = 'Liquidation sits very close to your stop — a fast move could trigger it before your stop fills.';
  } else {
    message = `Liquidation sits ${formatR(actualR)}R beyond your stop — you'd be at −${formatR(lossAtLiqR)}R if it's ever hit (normally you exit at your stop, −1R).`;
  }

  return { actualR, lossAtLiqR, message, tone, beyond };
}
